import React, { useState, useEffect } from 'react';
import { queueAPI, messageAPI, whatsappAPI, campaignAPI } from '../api';

function Monitoring() {
  const [stats, setStats] = useState(null);
  const [logs, setLogs] = useState([]);
  const [scheduledCampaigns, setScheduledCampaigns] = useState([]);
  const [activeCampaigns, setActiveCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingSchedule, setEditingSchedule] = useState(null);
  
  useEffect(() => {
    loadStats();
    loadLogs();
    loadScheduledCampaigns();
    loadActiveCampaigns();

    // Refresh every 5 seconds
    const interval = setInterval(() => {
      loadStats();
      loadScheduledCampaigns();
      loadActiveCampaigns();
    }, 5000);

    return () => clearInterval(interval);
  }, []);

  const loadStats = async () => {
    try {
      const response = await queueAPI.getStats();
      setStats(response.data);
    } catch (error) {
      console.error('Error loading stats:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadLogs = async () => {
    try {
      // Load recent message logs (you can modify this to fetch health logs if you create that table)
      const response = await messageAPI.getAll();
      setLogs(response.data.slice(0, 20));
    } catch (error) {
      console.error('Error loading logs:', error);
    }
  };
  
  const loadScheduledCampaigns = async () => {
    try {
      const response = await whatsappAPI.getScheduled();
      setScheduledCampaigns(response.data);
    } catch (error) {
      console.error('Error loading scheduled campaigns:', error);
    }
  };
  
  const loadActiveCampaigns = async () => {
    try {
      // Get campaigns with active jobs in queue
      const response = await campaignAPI.getAll();
      // For now, show campaigns as potentially active if they exist
      // In a more sophisticated setup, you'd track which campaigns have jobs in queue
      setActiveCampaigns(response.data.slice(0, 5)); // Show top 5
    } catch (error) {
      console.error('Error loading active campaigns:', error);
    }
  };

  const handleClearQueue = async () => {
    if (window.confirm('Clear the entire message queue? This cannot be undone.')) {
      try {
        await queueAPI.clear();
        loadStats();
        alert('Queue cleared successfully');
      } catch (error) {
        alert('Failed to clear queue');
      }
    }
  };
  
  const handleCancelSchedule = async (id, campaignName) => {
    if (window.confirm(`Cancel scheduled campaign "${campaignName}"?`)) {
      try {
        await whatsappAPI.cancelScheduled(id);
        await loadScheduledCampaigns();
        alert('Schedule cancelled successfully');
      } catch (error) {
        alert('Failed to cancel schedule: ' + error.message);
      }
    }
  };
  
  const handleEditSchedule = (schedule) => {
    // Time is stored as IST, convert to datetime-local format
    const istTime = schedule.scheduled_time.replace(' ', 'T').substring(0, 16);
    
    setEditingSchedule({
      ...schedule,
      newTime: istTime
    });
  };
  
  const handleSaveSchedule = async () => {
    if (!editingSchedule) return;
    
    try {
      await whatsappAPI.updateScheduled(editingSchedule.id, editingSchedule.newTime);
      setEditingSchedule(null);
      await loadScheduledCampaigns();
      alert('Schedule updated successfully');
    } catch (error) {
      alert('Failed to update schedule: ' + error.message);
    }
  };
  
  const handleStopCampaign = async (id, name) => {
    if (window.confirm(`Stop campaign "${name}"? This will remove all pending messages from the queue.`)) {
      try {
        const response = await campaignAPI.stop(id);
        alert(response.data.message);
        await loadStats();
      } catch (error) {
        alert('Failed to stop campaign: ' + error.message);
      }
    }
  };
  
  const formatDateTime = (istTime) => {
    if (!istTime) return 'N/A';
    // If the timestamp doesn't include timezone, assume it's IST
    let dateStr = istTime;
    if (!istTime.includes('+') && !istTime.includes('Z')) {
      // Add IST timezone offset
      dateStr = istTime.replace(' ', 'T') + '+05:30';
    }
    const date = new Date(dateStr);
    return date.toLocaleString('en-IN', { 
      timeZone: 'Asia/Kolkata',
      year: 'numeric',
      month: '2-digit', 
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: true
    });
  };
  
  const getTimeUntil = (istTime) => {
    if (!istTime) return '';
    const scheduled = new Date(istTime); // Parse as local IST
    const now = new Date();
    const diff = scheduled - now;
    
    if (diff < 0) return 'Past due';
    
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const mins = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
    
    if (hours > 24) {
      const days = Math.floor(hours / 24);
      return `in ${days}d ${hours % 24}h`;
    }
    return `in ${hours}h ${mins}m`;
  };

  if (loading) {
    return <div className="container"><div className="loading">Loading monitoring data...</div></div>;
  }

  const dailyLimit = process.env.REACT_APP_DAILY_LIMIT || 200;
  const usagePercent = stats?.daily?.messages_sent ? 
    Math.round((stats.daily.messages_sent / dailyLimit) * 100) : 0;

  return (
    <div className="container">
      <h1>Monitoring & Queue Dashboard</h1>

      {/* Scheduled Campaigns Section */}
      <div className="card">
        <h2>📅 Scheduled Campaigns</h2>
        {scheduledCampaigns.length === 0 ? (
          <p style={{ color: '#666', textAlign: 'center', padding: '20px' }}>
            No scheduled campaigns
          </p>
        ) : (
          <div className="table-container">
            <table className="table">
              <thead>
                <tr>
                  <th>Campaign</th>
                  <th>Scheduled For</th>
                  <th>Time Until</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {scheduledCampaigns.map(schedule => (
                  <tr key={schedule.id}>
                    <td><strong>{schedule.campaign_name}</strong></td>
                    <td>{formatDateTime(schedule.scheduled_time)}</td>
                    <td>{getTimeUntil(schedule.scheduled_time)}</td>
                    <td>
                      <span className={`badge ${
                        schedule.status === 'pending' ? 'badge-warning' :
                        schedule.status === 'completed' ? 'badge-success' :
                        schedule.status === 'failed' ? 'badge-danger' :
                        'badge-secondary'
                      }`}>
                        {schedule.status}
                      </span>
                    </td>
                    <td>
                      {schedule.status === 'pending' && (
                        <div style={{ display: 'flex', gap: '8px' }}>
                          <button 
                            className="btn btn-sm btn-primary"
                            onClick={() => handleEditSchedule(schedule)}
                            title="Edit schedule"
                          >
                            ✏️ Edit
                          </button>
                          <button 
                            className="btn btn-sm btn-danger"
                            onClick={() => handleCancelSchedule(schedule.id, schedule.campaign_name)}
                            title="Cancel schedule"
                          >
                            ❌ Cancel
                          </button>
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
      
      {/* Edit Schedule Modal */}
      {editingSchedule && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div className="card" style={{ maxWidth: '500px', margin: '20px' }}>
            <h3>Edit Schedule: {editingSchedule.campaign_name}</h3>
            <div style={{ marginBottom: '15px' }}>
              <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
                New Scheduled Time:
              </label>
              <input
                type="datetime-local"
                className="input"
                value={editingSchedule.newTime}
                onChange={(e) => setEditingSchedule({
                  ...editingSchedule,
                  newTime: e.target.value
                })}
              />
              <small style={{ display: 'block', marginTop: '5px', color: '#666' }}>
                Current: {formatDateTime(editingSchedule.scheduled_time)}
              </small>
            </div>
            <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
              <button 
                className="btn btn-secondary"
                onClick={() => setEditingSchedule(null)}
              >
                Cancel
              </button>
              <button 
                className="btn btn-primary"
                onClick={handleSaveSchedule}
              >
                Save Changes
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="grid">
        <div className="stat-card">
          <h3>Messages in Queue</h3>
          <div className="stat-value">{(stats?.queue?.waiting || 0) + (stats?.queue?.delayed || 0)}</div>
          <small>Waiting: {stats?.queue?.waiting || 0} | Delayed: {stats?.queue?.delayed || 0}</small>
        </div>
        <div className="stat-card">
          <h3>Active Processing</h3>
          <div className="stat-value" style={{ color: '#25d366' }}>{stats?.queue?.active || 0}</div>
        </div>
        <div className="stat-card">
          <h3>Completed Today</h3>
          <div className="stat-value">{stats?.queue?.completed || 0}</div>
        </div>
        <div className="stat-card">
          <h3>Failed</h3>
          <div className="stat-value" style={{ color: '#dc3545' }}>{stats?.queue?.failed || 0}</div>
        </div>
      </div>

      <div className="card">
        <h2>Daily Stats</h2>
        <div className="stats-grid">
          <div className="stat-item">
            <div className="stat-label">Messages Sent Today</div>
            <div className="stat-number">{stats?.daily?.messages_sent || 0}</div>
            <div className="progress-bar">
              <div 
                className="progress-fill" 
                style={{ 
                  width: `${usagePercent}%`,
                  backgroundColor: usagePercent >= 100 ? '#dc3545' : usagePercent >= 80 ? '#ffc107' : '#25d366'
                }}
              />
            </div>
            <small>{usagePercent}% of daily limit ({dailyLimit})</small>
          </div>

          <div className="stat-item">
            <div className="stat-label">Delivered</div>
            <div className="stat-number">{stats?.daily?.messages_delivered || 0}</div>
          </div>

          <div className="stat-item">
            <div className="stat-label">Read</div>
            <div className="stat-number">{stats?.daily?.messages_read || 0}</div>
          </div>

          <div className="stat-item">
            <div className="stat-label">Replies Received</div>
            <div className="stat-number">{stats?.daily?.replies_received || 0}</div>
          </div>
        </div>
        
        {usagePercent >= 90 && (
          <div className="error" style={{ marginTop: '15px' }}>
            ⚠️ Warning: Daily limit almost reached! Messages will be automatically paused at {dailyLimit}.
          </div>
        )}
      </div>

      {/* Stop Active Campaigns Section */}
      {((stats?.queue?.waiting || 0) + (stats?.queue?.delayed || 0)) > 0 && (
        <div className="card">
          <h2>🛑 Stop Active Campaigns</h2>
          <p style={{ color: '#666', marginBottom: '15px' }}>
            Stop campaigns that are currently queued or processing. This removes all pending messages for the selected campaign from the queue.
          </p>
          {activeCampaigns.length > 0 && (
            <div style={{ display: 'grid', gap: '10px' }}>
              {activeCampaigns.slice(0, 10).map(campaign => (
                <div key={campaign.id} style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  padding: '10px',
                  border: '1px solid #ddd',
                  borderRadius: '5px'
                }}>
                  <div>
                    <strong>{campaign.name}</strong>
                    <div style={{ fontSize: '0.85em', color: '#666' }}>
                      Campaign ID: {campaign.id}
                    </div>
                  </div>
                  <button 
                    className="btn btn-danger"
                    onClick={() => handleStopCampaign(campaign.id, campaign.name)}
                  >
                    🛑 Stop Campaign
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      <div className="card">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
          <h2>Queue Management</h2>
          <button className="btn btn-danger" onClick={handleClearQueue}>
            Clear Queue
          </button>
        </div>
        <p style={{ color: '#666', marginBottom: '15px' }}>
          Messages are queued with randomized delays (30-90 seconds) to mimic human behavior and avoid rate limits.
        </p>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '15px' }}>
          <div>
            <strong>Waiting in Queue:</strong> {stats?.queue?.waiting || 0}
          </div>
          <div>
            <strong>Delayed (Scheduled):</strong> {stats?.queue?.delayed || 0}
          </div>
          <div>
            <strong>Currently Processing:</strong> {stats?.queue?.active || 0}
          </div>
          <div>
            <strong>Completed:</strong> {stats?.queue?.completed || 0}
          </div>
          <div>
            <strong>Failed:</strong> {stats?.queue?.failed || 0}
          </div>
        </div>
      </div>

      <div className="card">
        <h2>Features Active</h2>
        <ul style={{ listStyle: 'none', padding: 0 }}>
          <li style={{ padding: '8px 0', borderBottom: '1px solid #eee' }}>
            ✅ Random Jitter (30-90 seconds between messages)
          </li>
          <li style={{ padding: '8px 0', borderBottom: '1px solid #eee' }}>
            ✅ Simulated Typing & Presence
          </li>
          <li style={{ padding: '8px 0', borderBottom: '1px solid #eee' }}>
            ✅ Auto-Pause on Reply
          </li>
          <li style={{ padding: '8px 0', borderBottom: '1px solid #eee' }}>
            ✅ Blacklist Filtering
          </li>
          <li style={{ padding: '8px 0', borderBottom: '1px solid #eee' }}>
            ✅ Daily Throttling ({dailyLimit} messages/day)
          </li>
          <li style={{ padding: '8px 0' }}>
            ✅ Delivery & Read Tracking
          </li>
        </ul>
      </div>
    </div>
  );
}

export default Monitoring;
